import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { computed, flow, observable, runInAction, set } from "mobx";
import { ESubscriptionStatus, routeLocationAffiliateGet, routeLocationInvoicesGet, routeLocationLicensesGet, routeLocationSubscriptionsGet, routeLocationUserGet, routeLocationUserPatch, routeLocationUserSessionDelete, routeLocationUserSessionPatch } from "@devowl-wp/api-real-commerce";
import { Affiliate } from "./affiliate.js";
import { Installation } from "./installation.js";
import { Invoice } from "./invoice.js";
import { License } from "./license.js";
import { PaddleSubscription } from "./paddleSubscription.js";
import { RpmProductVariantToSubscription } from "./rpmProductVariantToSubscription.js";
import { RealCommerceError } from "../exceptions/realCommerceError.js";
import { getJwtPayload } from "../utils/getJwtPayload.js";
import { realCommerceRequest } from "../utils/realCommerceRequest.js";
class Session {
    get authorizationHeader() {
        return {
            Authorization: `Bearer ${this.jwt}`
        };
    }
    get canSignUpAsAffiliate() {
        var _this_affiliateError, // Pending and blocked also has 401 error code, so we need to check if it is an empty response
        _this_affiliateError_codes;
        return ((_this_affiliateError = this.affiliateError) == null ? void 0 : _this_affiliateError.status) === 401 && ((_this_affiliateError_codes = this.affiliateError.codes) == null ? void 0 : _this_affiliateError_codes.length) === 0 && !this.affiliate;
    }
    get jwtPayload() {
        return getJwtPayload(this.jwt);
    }
    get activeSubscriptions() {
        const res = Array.from(this.subscriptions.values()).filter((s)=>[
                ESubscriptionStatus.Active,
                ESubscriptionStatus.CancellationPending,
                ESubscriptionStatus.Canceled
            ].indexOf(s.data.status) > -1);
        res.sort((a, b)=>a.sortIndex - b.sortIndex);
        return res;
    }
    get subscriptionProductIds() {
        return Array.from(this.subscriptions.values()).map((param)=>{
            let { data: { productPlan: { product: { id } } } } = param;
            return id;
        });
    }
    constructor(jwt, store){
        this.busy = false;
        this.busyAffiliate = false;
        this.busyLicenses = false;
        this.busySubscriptions = false;
        this.busyInvoices = false;
        this.hasEnvatoPurchase = false;
        this.licenses = new Map();
        this.subscriptions = new Map();
        this.installations = new Map();
        this.rpmProductVariantToSubscription = new Map();
        this.invoices = [];
        this.fetchUser = flow(function*() {
            this.busy = true;
            try {
                const response = yield realCommerceRequest({
                    location: routeLocationUserGet,
                    settings: {
                        headers: this.authorizationHeader
                    }
                });
                this.user = response.user;
                try {
                    yield this.fetchAffiliate();
                } catch (e) {
                // Error handling is done via affiliate status properties within this class
                }
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            } finally{
                this.busy = false;
            }
        });
        this.fetchAffiliate = flow(function*() {
            this.busyAffiliate = true;
            try {
                const response = yield realCommerceRequest({
                    location: routeLocationAffiliateGet,
                    settings: {
                        headers: this.authorizationHeader
                    }
                });
                if (this.affiliate) {
                    set(this.affiliate.data, response);
                } else {
                    this.affiliate = new Affiliate(response, this);
                }
                this.affiliateError = undefined;
            } catch (e) {
                console.log(e);
                this.affiliateError = new RealCommerceError(e);
                throw this.affiliateError;
            } finally{
                this.busyAffiliate = false;
            }
        });
        this.fetchSubscriptionsAndLicenses = flow(function*() {
            // Do not fetch in concurrent, as this could lead to longer times as both requests are contacting the same db tables
            yield this.fetchSubscriptions();
            yield this.fetchLicenses();
        });
        this.updateDetails = flow(function*(user) {
            this.busy = true;
            yield this.probablyRenewJwt();
            try {
                const response = yield realCommerceRequest({
                    location: routeLocationUserPatch,
                    request: {
                        user
                    },
                    settings: {
                        headers: this.authorizationHeader
                    }
                });
                this.user = response.user;
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            } finally{
                this.busy = false;
            }
        });
        this.logout = flow(function*() {
            try {
                yield realCommerceRequest({
                    location: routeLocationUserSessionDelete,
                    settings: {
                        headers: this.authorizationHeader
                    }
                });
            } catch (e) {
                throw new RealCommerceError(e);
            }
        });
        this.fetchInvoices = flow(function*() {
            this.busyInvoices = true;
            this.invoices = [];
            yield this.probablyRenewJwt();
            try {
                const response = yield realCommerceRequest({
                    location: routeLocationInvoicesGet,
                    settings: {
                        headers: this.authorizationHeader
                    }
                });
                for (const row of response.invoices){
                    this.invoices.push(new Invoice(row, this));
                }
                this.hasEnvatoPurchase = response.hasEnvatoPurchase;
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            } finally{
                this.busyInvoices = false;
            }
        });
        /**
     * When the JWT gets expired soon, let's try to regenerate it, otherwise logout.
     */ this.probablyRenewJwt = flow(function*(force) {
            if (this.jwtPayload.expired || force) {
                try {
                    const { jwt } = yield realCommerceRequest({
                        location: routeLocationUserSessionPatch,
                        settings: {
                            headers: this.authorizationHeader
                        }
                    });
                    this.jwt = jwt;
                } catch (e) {
                    console.log(e);
                    throw new RealCommerceError(e);
                }
            }
        });
        this.fetchLicenses = flow(function*() {
            this.busyLicenses = true;
            yield this.probablyRenewJwt();
            try {
                const { licenses, rpmProductVariants } = yield realCommerceRequest({
                    location: routeLocationLicensesGet,
                    settings: {
                        headers: this.authorizationHeader
                    }
                });
                for (const row of licenses){
                    const { id } = row;
                    const existing = this.licenses.get(id);
                    if (existing) {
                        set(existing.data, row);
                    } else {
                        this.licenses.set(id, new License(row, this));
                    }
                }
                // Remove stale licenses
                const activeIds = licenses.map((param)=>{
                    let { id } = param;
                    return id;
                });
                Array.from(this.licenses.keys()).forEach((potentialStaleId)=>{
                    if (activeIds.indexOf(potentialStaleId) === -1) {
                        this.licenses.delete(potentialStaleId);
                    }
                });
                this.rpmProductVariantToSubscription.clear();
                for (const { subscription: { id }, productVariant } of rpmProductVariants){
                    const rpmProductVariantToSubscription = this.rpmProductVariantToSubscription.get(id) || [];
                    rpmProductVariantToSubscription.push(new RpmProductVariantToSubscription(productVariant, this));
                    this.rpmProductVariantToSubscription.set(id, rpmProductVariantToSubscription);
                }
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            } finally{
                this.busyLicenses = false;
            }
        });
        this.fetchSubscriptions = flow(function*() {
            this.busySubscriptions = true;
            yield this.probablyRenewJwt();
            try {
                const { subscriptions, installations } = yield realCommerceRequest({
                    location: routeLocationSubscriptionsGet,
                    settings: {
                        headers: this.authorizationHeader
                    }
                });
                let i = 0;
                for (const row of subscriptions){
                    const { id } = row;
                    const existing = this.subscriptions.get(id);
                    if (existing) {
                        set(existing.data, row);
                    } else {
                        // TODO: differ between ecommerce systems
                        this.subscriptions.set(id, new PaddleSubscription(row, this));
                    }
                    this.subscriptions.get(id).sortIndex = i;
                    i++;
                }
                // Remove stale subscriptions
                const activeIds = subscriptions.map((param)=>{
                    let { id } = param;
                    return id;
                });
                Array.from(this.subscriptions.keys()).forEach((potentialStaleId)=>{
                    if (activeIds.indexOf(potentialStaleId) === -1) {
                        this.subscriptions.delete(potentialStaleId);
                    }
                });
                this.installations.clear();
                for (const row of installations){
                    const installationsToSubscriptions = this.installations.get(row.subscriptionId) || [];
                    installationsToSubscriptions.push(new Installation(row, this));
                    this.installations.set(row.subscriptionId, installationsToSubscriptions);
                }
            } catch (e) {
                console.log(e);
                throw new RealCommerceError(e);
            } finally{
                this.busySubscriptions = false;
            }
        });
        this.store = store;
        runInAction(()=>{
            this.jwt = jwt;
        });
    }
}
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Session.prototype, "user", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Affiliate === "undefined" ? Object : Affiliate)
], Session.prototype, "affiliate", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof RealCommerceError === "undefined" ? Object : RealCommerceError)
], Session.prototype, "affiliateError", void 0);
_ts_decorate([
    observable
], Session.prototype, "busy", void 0);
_ts_decorate([
    observable
], Session.prototype, "busyAffiliate", void 0);
_ts_decorate([
    observable
], Session.prototype, "busyLicenses", void 0);
_ts_decorate([
    observable
], Session.prototype, "busySubscriptions", void 0);
_ts_decorate([
    observable
], Session.prototype, "busyInvoices", void 0);
_ts_decorate([
    observable
], Session.prototype, "hasEnvatoPurchase", void 0);
_ts_decorate([
    observable
], Session.prototype, "licenses", void 0);
_ts_decorate([
    observable
], Session.prototype, "subscriptions", void 0);
_ts_decorate([
    observable
], Session.prototype, "installations", void 0);
_ts_decorate([
    observable
], Session.prototype, "rpmProductVariantToSubscription", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Array)
], Session.prototype, "invoices", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", String)
], Session.prototype, "jwt", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], Session.prototype, "authorizationHeader", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], Session.prototype, "canSignUpAsAffiliate", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], Session.prototype, "jwtPayload", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], Session.prototype, "activeSubscriptions", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", []),
    _ts_metadata("design:returntype", void 0)
], Session.prototype, "subscriptionProductIds", null);
export { Session };
